import { ToggleGroup, ToggleGroupItem } from '@billy/design-system'
import { ChangeEvent, ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { LabsFlag } from '../../../../enums/labsFlag'
import { Scope } from '../../../../enums/scope'
import { useSegment } from '../../../../hooks'
import { useOrganizationViewUrl } from '../../../../hooks/routing/useOrganizationViewUrl'
import { useAdvisorAccess } from '../../../../hooks/useAdvisorAccess'
import { useLabsMenuFlags } from '../../../../hooks/useLabsMenuFlags'
import { isAuthorized } from '../../../../utils/isAuthorized'
import { reactRoute } from '../../../../utils/routing/reactRoute'
import { useAdvisorLayerModeContext } from '../AdvisorLayer/contexts/advisorLayerModeContext'
import { AdvisorLayerMode } from '../AdvisorLayer/enums/advisorLayerMode'
import { isAdvisorLayerMode } from '../AdvisorLayer/utils/isAdvisorLayerMode'
import * as Styled from './styles'

export const AdvisorToggle = (): ReactElement | null => {
  const { t } = useTranslation()
  const { shouldShowToggle } = useAdvisorAccess()
  const { mode, setMode } = useAdvisorLayerModeContext()
  const { track } = useSegment()
  const { organization } = useUserOrganization()
  const navigate = useNavigate()
  const labsFlags = useLabsMenuFlags()
  const { url: expertDocumentsUrl } = useOrganizationViewUrl(reactRoute.organization.documents.getMainRoute())
  const { url: expertMyClientUrl } = useOrganizationViewUrl(reactRoute.organization.myClient.getMainRoute())
  const { url: dashboardUrl } = useOrganizationViewUrl(reactRoute.organization.dashboard.getMainRoute())
  const { url: expertDashboardUrl } = useOrganizationViewUrl(reactRoute.organization.dashboard.getMainRoute(), {
    advisorLayer: AdvisorLayerMode.Expert,
  })
  // My Client route only exists when the labs flag is on, otherwise keep Expert mode on the dashboard
  const expertFallbackUrl = labsFlags[LabsFlag.NavigationMyClient] ? expertMyClientUrl : expertDashboardUrl
  const expertUrl = isAuthorized(Scope.DmsFileRead) ? expertDocumentsUrl : expertFallbackUrl

  const items = useMemo<ToggleGroupItem[]>(
    () => [
      { value: AdvisorLayerMode.Expert, label: t('navigation.advisor_toggle.expert') },
      { value: AdvisorLayerMode.Client, label: t('navigation.advisor_toggle.client_view') },
    ],
    [t],
  )

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.currentTarget
      if (!isAdvisorLayerMode(value)) {
        return
      }
      const targetUrl = value === AdvisorLayerMode.Expert ? expertUrl : dashboardUrl
      if (!targetUrl) {
        return
      }
      setMode(value)
      navigate(targetUrl, { replace: true })
      track('Advisor Mode Toggled (Client)', {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        to_navigation_mode: value,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        client_id: organization?.id,
      })
    },
    [setMode, track, organization?.id, expertUrl, dashboardUrl, navigate],
  )

  if (!shouldShowToggle) {
    return null
  }

  return (
    <Styled.AdvisorToggleWrapper data-testid="advisor-toggle">
      <ToggleGroup items={items} name="advisor-layer-mode" onChange={handleChange} value={mode} />
    </Styled.AdvisorToggleWrapper>
  )
}
